// ── Registro de cambios de texto pendientes ──────────────────────────────────
//
// Separado del hook a propósito: acá vive la lógica que se puede equivocar
// (contra qué original se compara, qué pasa al editar dos veces, volver atrás a
// mano) y no depende del DOM, así que se puede testear sin montar React.
//
// Es la fuente de verdad. El hook mantiene una copia en estado solo para que la
// UI re-renderice; nunca al revés. Eso importa al guardar: un handler que acaba
// de cerrar una edición lee de acá y ve el último cambio, mientras que el estado
// de React todavía va un tick atrás.

export interface TextEdit {
  targetId: string;
  original: string;
  value: string;
  /**
   * 'full' reemplaza todo el contenido; 'own' solo el texto propio, dejando los
   * elementos hijos intactos. Determina qué operación se manda al servidor.
   */
  kind: 'rich' | 'own';
}

export class TextEditLedger {
  /** Texto original por elemento, de la PRIMERA vez que se tocó. */
  private originals = new Map<string, { text: string; kind: 'rich' | 'own' }>();
  private edits: TextEdit[] = [];

  /**
   * Registra (o devuelve) el texto original del elemento.
   *
   * Si se edita dos veces, el original sigue siendo el de la primera: comparar
   * contra el texto intermedio haría que volver a mano al texto de partida
   * quedara guardado como un cambio.
   */
  noteOriginal(targetId: string, currentText: string, kind: 'rich' | 'own'): string {
    const known = this.originals.get(targetId);
    if (known !== undefined) return known.text;
    this.originals.set(targetId, { text: currentText, kind });
    return currentText;
  }

  /** Anota el texto final de un elemento. Volver al original lo saca de la lista. */
  record(targetId: string, value: string): void {
    const known = this.originals.get(targetId);
    if (known === undefined) return;
    this.edits = this.edits.filter(e => e.targetId !== targetId);
    if (value !== known.text) {
      this.edits.push({ targetId, original: known.text, value, kind: known.kind });
    }
  }

  list(): TextEdit[] {
    return [...this.edits];
  }

  get size(): number {
    return this.edits.length;
  }

  clear(): void {
    this.originals.clear();
    this.edits = [];
  }
}

/** Normaliza el texto leído del DOM: colapsa espacios y recorta. */
export function normalizeEditableText(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\s+/g, ' ').trim();
}

/** Cómo se puede editar un elemento, o por qué no se puede. */
export type Editability =
  /**
   * Bloque de texto: todo lo que contiene sobrevive a la reconstrucción del
   * servidor, así que se puede editar entero con formato.
   */
  | { kind: 'rich' }
  /**
   * Tiene adentro algo que la reconstrucción NO conserva (un ícono, una imagen).
   * Se toca solo su texto propio, que es lo que deja el resto intacto.
   */
  | { kind: 'own' }
  | { kind: 'none'; reason: EditabilityBlock };

/** Por qué un elemento no se puede editar. Cada valor tiene su texto en i18n. */
export type EditabilityBlock =
  /** Contiene bloques: su texto pertenece a los elementos de adentro. */
  | 'container'
  /**
   * Mezcla texto con algo que no se puede conservar, en más de un trozo. No hay
   * forma de saber a cuál va lo que se escriba.
   */
  | 'mixed'
  /** No hay texto: una imagen, un ícono, un separador. */
  | 'no-text'
  /** No forma parte del contenido direccionable. */
  | 'not-addressable';

/** Lo mínimo que hace falta de un elemento del DOM, para poder testear sin DOM. */
export interface EditableProbe {
  tagName: string;
  /** Tags de los hijos que son elementos, en orden. */
  childTags: string[];
  getAttribute: (name: string) => string | null;
  /** Trozos de texto que pertenecen al elemento mismo, sin contar descendientes. */
  ownTextChunks: string[];
  /** Texto completo, incluidos los descendientes. */
  fullText: string;
}

/** Elementos que nunca son texto. */
const NEVER_EDITABLE = new Set(['img', 'br', 'hr', 'input', 'svg', 'video', 'iframe', 'canvas']);

/**
 * Hijos que sobreviven la reconstrucción del servidor (rebuildRichText).
 *
 * Esta lista tiene que seguir a la del servidor, no a la intuición de qué es
 * "inline". Si acá entrara <a> o <span> sin que el servidor los conserve, editar
 * el bloque BORRARÍA ese hijo al guardar — se vería un cambio en pantalla y otro
 * en el archivo.
 */
/**
 * Etiquetas entre las que se puede convertir un texto. Tiene que coincidir con
 * TEXT_TAGS del servidor: si acá se ofreciera una que allá no está, el cambio se
 * vería en la vista previa y el guardado lo rechazaría.
 */
export const TEXT_TAGS: string[] = ['p', 'h1', 'h2', 'h3', 'h4'];

const SURVIVES_REBUILD = new Set(['strong', 'b', 'br', 'ul', 'li']);

/** Etiquetas que arman estructura: su presencia hace que el elemento sea contenedor. */
const BLOCK_TAGS = new Set([
  'div', 'section', 'article', 'header', 'footer', 'nav', 'aside', 'main',
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'form', 'table', 'figure', 'ol', 'dl',
]);

/**
 * ¿Cómo se puede editar este elemento?
 *
 * Se clasifica por TIPO DE CONTENIDO, no por estructura del DOM. La versión
 * anterior miraba "¿tiene hijos? ¿cuántos nodos de texto?", y con eso apretar
 * Enter —que inserta un <br>— sacaba al párrafo del conjunto editable. Un párrafo
 * con saltos de línea sigue siendo un párrafo.
 */
export function classifyEditability(el: EditableProbe): Editability {
  if (!el.getAttribute('data-evo-id')) return { kind: 'none', reason: 'not-addressable' };

  const tag = el.tagName.toLowerCase();
  if (NEVER_EDITABLE.has(tag)) return { kind: 'none', reason: 'no-text' };
  if (el.fullText.trim() === '') return { kind: 'none', reason: 'no-text' };

  const hijos = el.childTags.map(t => t.toLowerCase());
  if (hijos.some(h => BLOCK_TAGS.has(h))) return { kind: 'none', reason: 'container' };

  // Sin hijos, o solo con hijos que la reconstrucción conserva.
  if (hijos.every(h => SURVIVES_REBUILD.has(h))) return { kind: 'rich' };

  // Hay algo que no se conserva (un <svg>, un <a>): solo se toca el texto propio.
  const propios = el.ownTextChunks.filter(t => t.trim() !== '');
  if (propios.length === 1) return { kind: 'own' };
  return { kind: 'none', reason: 'mixed' };
}

/** Lee de un elemento del DOM lo que classifyEditability necesita. */
export function probeOf(el: Element): EditableProbe {
  const chunks: string[] = [];
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === 3) chunks.push(node.textContent ?? '');
  }
  return {
    tagName: el.tagName,
    childTags: Array.from(el.children).map(c => c.tagName),
    getAttribute: (n) => el.getAttribute(n),
    ownTextChunks: chunks,
    fullText: el.textContent ?? '',
  };
}

/**
 * El valor que se compara y se guarda, según cómo sea editable el elemento.
 *
 * En una hoja se toma el HTML interno, no el texto: es lo que permite conservar
 * el formato acotado (negrita, salto de línea, viñetas). El servidor lo
 * reconstruye desde su lista blanca antes de escribirlo, así que lo que salga de
 * acá no puede meter markup que no esté permitido.
 *
 * En un elemento con hijos se toma solo el texto propio: ahí el formato no aplica
 * porque no se puede tocar lo que rodea a los hijos sin ambigüedad.
 */
export function editableValueOf(el: Element, kind: 'rich' | 'own'): string {
  if (kind === 'rich') return (el.innerHTML ?? '').trim();
  return normalizeEditableText(probeOf(el).ownTextChunks.join(''));
}
